import { useState, useEffect } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Progress } from "../../components/ui/progress";
import { Upload, Check, AlertCircle, Loader2, File, ExternalLink } from "lucide-react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

const NDAUploadSection = ({ title, description, settingsKey, currentUrl, onUploaded }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadError, setUploadError] = useState("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Only PDF files are allowed"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("File size must be less than 25 MB"); return; }
    setSelectedFile(file);
    setUploadStatus(null);
    setUploadError("");
    setUploadProgress(0);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadStatus(null);
    setUploadError("");
    setUploadProgress(0);

    try {
      const headers = getAuthHeaders();

      const presignResponse = await axios.post(
        `${API}/uploads/presign`,
        { filename: selectedFile.name, content_type: "application/pdf", category: "nda" },
        { withCredentials: true, headers }
      );
      const { upload_url, fields, storage_path } = presignResponse.data;
      setUploadProgress(30);

      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
      formData.append("file", selectedFile);
      await axios.post(upload_url, formData, {
        onUploadProgress: (e) => setUploadProgress(Math.min(Math.round((e.loaded * 40) / e.total + 30), 70)),
      });
      setUploadProgress(70);

      await axios.post(
        `${API}/settings/`,
        { [settingsKey]: storage_path },
        { withCredentials: true, headers }
      );

      setUploadProgress(100);
      setUploadStatus("success");
      toast.success(`${title} uploaded successfully!`);
      onUploaded();

      setTimeout(() => { setSelectedFile(null); setUploadProgress(0); setUploadStatus(null); }, 2000);
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || "Upload failed. Please try again.";
      setUploadError(msg);
      setUploadStatus("error");
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
      </div>

      <div className="bg-card border rounded-lg p-6 space-y-4">
        <div>
          <Label htmlFor={`file-${settingsKey}`} className="text-sm font-medium mb-2 block">
            PDF Document <span className="text-red-500">*</span>
          </Label>
          <Input
            id={`file-${settingsKey}`}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            disabled={uploading}
            className="cursor-pointer file:cursor-pointer"
          />
          <p className="text-xs text-muted-foreground mt-1.5">PDF only, max 25 MB</p>
        </div>

        {currentUrl && (
          <div className="flex items-center gap-2 text-sm">
            <File className="w-4 h-4 text-blue-500 shrink-0" />
            <span className="text-muted-foreground">Current file:</span>
            <a href={currentUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline flex items-center gap-1">
              View document <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        {selectedFile && (
          <div className="bg-muted/50 rounded-lg p-3 flex items-start gap-3">
            <File className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{selectedFile.name}</p>
              <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          </div>
        )}

        {uploading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Uploading...</p>
              <p className="text-xs text-muted-foreground">{uploadProgress}%</p>
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        )}

        {uploadStatus === "success" && (
          <Alert className="bg-green-500/10 border-green-500/20">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-600">Uploaded successfully!</AlertDescription>
          </Alert>
        )}

        {uploadStatus === "error" && (
          <Alert className="bg-red-500/10 border-red-500/20">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-600">{uploadError}</AlertDescription>
          </Alert>
        )}

        <Button onClick={handleUpload} disabled={!selectedFile || uploading} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" />Upload {title}</>
          )}
        </Button>
      </div>
    </div>
  );
};

const UploadPlatformNDAPage = () => {
  const [ndaUrl, setNdaUrl] = useState(null);
  const [dealNdaUrl, setDealNdaUrl] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`, { withCredentials: true, headers: getAuthHeaders() });
      if (res.data?.nda) setNdaUrl(res.data.nda);
      if (res.data?.deal_nda) setDealNdaUrl(res.data.deal_nda);
    } catch {}
  };

  useEffect(() => { fetchSettings(); }, []);

  return (
    <DashboardLayout title="NDA Documents">
      <div className="max-w-xl space-y-10">
        <NDAUploadSection
          title="Platform NDA"
          description="Required for Agents, Sellers, and Wholesalers on their first login. Admins and Investors are not prompted to sign this."
          settingsKey="nda"
          currentUrl={ndaUrl}
          onUploaded={fetchSettings}
        />

        <div className="border-t" />

        <NDAUploadSection
          title="Deal NDA"
          description="Signed by Investors when an admin assigns a deal to them. This document is attached to the deal at the time of assignment."
          settingsKey="deal_nda"
          currentUrl={dealNdaUrl}
          onUploaded={fetchSettings}
        />
      </div>
    </DashboardLayout>
  );
};

export default UploadPlatformNDAPage;
